import { useRequest, useParams, useLocation } from 'umi';
import React, { useState, useRef } from 'react';
import styles from '../css/dynamicCSS.less';
import cx from 'classnames';
import { DynamicType } from '@/types/camp/dynamic/dynamicType';
import { MessageOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import DynamicPraise from './dynamicPraise';
import { ReactComponent as RewardSvg } from '@/static/images/reward2.svg';
import { calcTimeString } from '@/utils';
import DynamicDetails from './dynamicDetails';
import { Button, message, Popconfirm, Input, InputNumber } from 'antd';
import type { PopconfirmProps } from 'antd';
import Editor from '@digit-fe/digit-editor-new';
import { editParseJson } from '@/utils/index';
import selectedPng from '@/static/images/selected.png';
import { ReactComponent as RocketSvg } from '@/static/images/rocket.svg';
import { postHide, postSelected } from '@/utils/requests/camp/requests_admin';

interface Props {
  item: DynamicType;
  index: number;
  setHash: (hash: number) => void;
}
const DynamicItem: React.FC<Props> = ({ item, index, setHash }) => {
  const [itemNew, setItemNew] = useState<DynamicType>(item);
  const [open, setOpen] = useState<boolean>(false);
  const location = useLocation();
  const { id: batchId } = useParams() as { id: string };
  const refScore = useRef<any>();
  const [openProp, setOpenProp] = useState<boolean>(false);

  // 隐藏
  const { run: postHideRequest } = useRequest(
    (hide) => {
      let params = {
        id: item.id,
        hide: hide,
      };
      return postHide(batchId, params);
    },
    {
      manual: true,
      onSuccess: (res) => {
        const newRes: any = {
          ...itemNew,
          hide: res.hide,
        };
        setItemNew(newRes);
        res.hide === 1 ? message.success('心得已设为隐藏') : message.success('心得已取消隐藏');
      },
    },
  );
  // 精选
  const { run: postSelectedRequest } = useRequest(
    ({ selected, score }) => {
      let params = {
        id: item.id,
        cardCampId: item.cardCampId,
        selected: selected,
        batchId: batchId,
        score: score,
        createdBy: item.createdBy,
      };
      return postSelected(batchId, params);
    },
    {
      manual: true,
      onSuccess: (res) => {
        const newRes: any = {
          ...itemNew,
          selected: res.selected,
        };
        setItemNew(newRes);
        res.selected === 1 ? message.success('心得已设为精选') : message.success('心得已取消精选');
      },
    },
  );

  const addSelected = () => {
    if (!refScore.current || refScore.current < 1) {
      message.warning('奖励分数必填');
      return false;
    } else {
      setOpenProp(false);
      postSelectedRequest({ selected: 1, score: refScore.current });
    }
  };

  return (
    <>
      <div key={index} className={styles.dynamic_box}>
        <div className={styles.item}>
          <div className={styles.lf}>
            <a href={`https://bbs.fanruan.com/u/${itemNew.createdBy}`} target="_blank">
              <img
                className={styles.face}
                src={`https://bbs.fanruan.com/uc_server/avatar.php?uid=${itemNew.createdBy}&amp;size=small`}
              ></img>
            </a>
          </div>
          <div className={styles.rt}>
            <div className={styles.user_name}>
              <a
                href={`https://bbs.fanruan.com/u/${itemNew.createdBy}`}
                className="col172 hover008 f16"
                target="_blank"
              >
                {itemNew.username}
              </a>
            </div>
            <div className={cx(styles.info, 'col808 f14')}>
              <span>{calcTimeString(itemNew.createdTime)}</span>
            </div>
            <div className={styles.info_text}>
              <Editor
                content={editParseJson(itemNew.content)}
                editable={false}
                titleProps={{ showTitle: false }}
                catalogProps={{ showCatalog: false }}
              />
            </div>

            <div>
              <span className={styles.node_name}>{itemNew.nodeName}</span>
            </div>

            <div className={cx(styles.tool, 'flex_3')}>
              <div className="flex_1">
                <div className="f14">
                  <DynamicPraise data={item} type={1} />
                </div>
                <div>
                  <Button
                    type="link"
                    onClick={() => setOpen(true)}
                    className={cx(styles.comment, 'flex_1 col808')}
                  >
                    <MessageOutlined />
                    <span>评论 {itemNew.commentsCount > 0 ? itemNew.commentsCount : ''}</span>
                  </Button>
                </div>

                {itemNew.rewardCredits > 0 && (
                  <div className={cx(styles.reward, 'flex_1')}>
                    <RewardSvg style={{ width: 15 }} />
                    <span className="colffa">奖励 {itemNew.rewardCredits} 学分</span>
                  </div>
                )}
              </div>
              {location.pathname === '/v2/camp/manage/' + batchId && (
                <div className="flex_1" style={{ gap: 40 }}>
                  {itemNew.selected === 1 ? (
                    <Popconfirm
                      title="确认取消该心得的精选吗？"
                      description="取消精选后将收回奖励的学分"
                      onConfirm={() => postSelectedRequest({ selected: 0, score: 0 })}
                      okText="确认"
                      cancelText="取消"
                    >
                      <div className="flex_1 col808 hover008 cur_p" style={{ gap: 6 }}>
                        <RocketSvg />
                        <span>取消精选</span>
                      </div>
                    </Popconfirm>
                  ) : (
                    <Popconfirm
                      key={itemNew.id}
                      open={openProp}
                      title="确认将该心得设为精选吗？"
                      description={
                        <div className="flex_1" style={{ padding: '10px 10px 10px 0' }}>
                          <span className="com_required"> 奖励：</span>
                          <InputNumber
                            key={itemNew.id}
                            style={{ width: 110 }}
                            min={1}
                            addonAfter="分"
                            onChange={(value) => {
                              refScore.current = value;
                            }}
                            changeOnWheel
                          />
                        </div>
                      }
                      onConfirm={() => addSelected()}
                      onCancel={() => setOpenProp(false)}
                      okText="确认"
                      cancelText="取消"
                    >
                      <div
                        onClick={() => {
                          setOpenProp(true);
                        }}
                        className="flex_1 col808 hover008 cur_p"
                        style={{ gap: 6 }}
                      >
                        <RocketSvg />
                        <span>设为精选</span>
                      </div>
                    </Popconfirm>
                  )}

                  {itemNew.hide == 1 ? (
                    <Popconfirm
                      title="确认取消隐藏该心得吗？"
                      description="取消隐藏后学员将能看到此动态"
                      onConfirm={() => postHideRequest(0)}
                      okText="确认"
                      cancelText="取消"
                    >
                      <div className="flex_1 col808 hover008 cur_p" style={{ gap: 6 }}>
                        <EyeInvisibleOutlined />
                        <span>取消隐藏</span>
                      </div>
                    </Popconfirm>
                  ) : (
                    <Popconfirm
                      title="确认隐藏该动态吗？"
                      onConfirm={() => postHideRequest(1)}
                      okText="隐藏"
                      cancelText="取消"
                    >
                      <div className="flex_1 col808 hover008 cur_p" style={{ gap: 6 }}>
                        <EyeInvisibleOutlined />
                        <span>设为隐藏</span>
                      </div>
                    </Popconfirm>
                  )}
                </div>
              )}
            </div>
          </div>
          {itemNew.selected == 1 && (
            <div className={styles.selected}>
              <img src={selectedPng} style={{ width: 80, height: 80 }} />
            </div>
          )}
          {itemNew.hide == 1 && (
            <div className={styles.selected}>
              <EyeInvisibleOutlined style={{ fontSize: 80, color: '#eee' }} />
            </div>
          )}
        </div>
      </div>
      {open && (
        <DynamicDetails key={item.id} item={item} open={open} setOpen={setOpen} setHash={setHash} />
      )}
    </>
  );
};
export default DynamicItem;
